import type { SupabaseClient } from '../supabase/client';
import type {
  StorefrontCustomProductCollectionLocalizedText,
  StorefrontPublicCustomProductCollectionPickerOption,
  StorefrontPublicProductPickerOption,
} from '../types/storefront-engine';
import {
  asRecord,
  emptyStorefrontCustomProductCollectionText,
  invokeStorefrontPublicContentRpc,
  parseRows,
  toCustomProductCollectionDetail,
  toCustomProductCollectionOption,
  type CustomProductCollectionRpcRow,
} from './storefront-public-content-picker.service';

export interface ListStorefrontCustomProductCollectionPickerOptionsInput {
  readonly storeId: string;
  readonly search?: string | null;
  readonly limit?: number;
}

export interface GetStorefrontCustomProductCollectionInput {
  readonly storeId: string;
  readonly collectionRef: string;
}

export interface UpsertStorefrontCustomProductCollectionInput {
  readonly storeId: string;
  readonly collectionRef?: string | null;
  readonly name: StorefrontCustomProductCollectionLocalizedText;
  readonly description?: StorefrontCustomProductCollectionLocalizedText | null;
  readonly productIds: readonly string[];
}

export interface StorefrontCustomProductCollectionAdminDetail extends StorefrontPublicCustomProductCollectionPickerOption {
  readonly products: readonly StorefrontPublicProductPickerOption[];
}

export async function listStorefrontCustomProductCollectionPickerOptions(
  client: SupabaseClient,
  input: ListStorefrontCustomProductCollectionPickerOptionsInput,
): Promise<StorefrontPublicCustomProductCollectionPickerOption[]> {
  const data = await invokeStorefrontPublicContentRpc(
    client,
    'list_storefront_custom_product_collection_picker_options',
    {
      target_store_id: input.storeId,
      search_query: input.search?.trim() || null,
      page_limit: input.limit ?? 20,
    },
  );
  return parseRows<CustomProductCollectionRpcRow>(data).flatMap((row) => {
    const option = toCustomProductCollectionOption(row);
    return option ? [option] : [];
  });
}

export async function getStorefrontCustomProductCollectionForAdmin(
  client: SupabaseClient,
  input: GetStorefrontCustomProductCollectionInput,
  options: { readonly supabaseUrl: string },
): Promise<StorefrontCustomProductCollectionAdminDetail | null> {
  return toCustomProductCollectionDetail(
    asRecord(
      await invokeStorefrontPublicContentRpc(
        client,
        'get_storefront_custom_product_collection_for_admin',
        {
          target_store_id: input.storeId,
          collection_ref: input.collectionRef,
        },
      ),
    ) as unknown as CustomProductCollectionRpcRow,
    options.supabaseUrl,
  );
}

export async function upsertStorefrontCustomProductCollectionForAdmin(
  client: SupabaseClient,
  input: UpsertStorefrontCustomProductCollectionInput,
  options: { readonly supabaseUrl: string },
): Promise<StorefrontCustomProductCollectionAdminDetail> {
  const detail = toCustomProductCollectionDetail(
    asRecord(
      await invokeStorefrontPublicContentRpc(
        client,
        'upsert_storefront_custom_product_collection_for_admin',
        {
          target_store_id: input.storeId,
          collection_ref: input.collectionRef ?? null,
          localized_name: input.name,
          localized_description: input.description ?? emptyStorefrontCustomProductCollectionText(),
          product_ids: input.productIds,
        },
      ),
    ) as unknown as CustomProductCollectionRpcRow,
    options.supabaseUrl,
  );
  if (!detail) {
    throw new Error('invalid custom product collection response');
  }
  return detail;
}

export const storefrontCustomProductCollectionsAdminService = {
  list: listStorefrontCustomProductCollectionPickerOptions,
  get: getStorefrontCustomProductCollectionForAdmin,
  upsert: upsertStorefrontCustomProductCollectionForAdmin,
};
